<script setup lang="ts">
import { computed } from 'vue'

/**
 * One metered resource's usage this period against what the plan includes (the Billing page), as an accessible meter. Its
 * tone says whether usage is under, at or over the included amount, from the `--zh-usage-meter-*` tokens in theme.css.
 * Where the plan includes an unlimited amount there is no maximum: usage is shown alone, and it is never over.
 */
const props = defineProps<{
    /** What the period has used so far. */
    used: number
    /** What the plan includes, or null where it includes an unlimited amount. */
    included: number | null
    /** The unit's label, for example "runner minutes". */
    unit: string
}>()

const tone = computed<'under' | 'at' | 'over'>(() => {
    if (props.included === null || props.used < props.included) {
        return 'under'
    }
    return props.used === props.included ? 'at' : 'over'
})

/** How much of the bar is filled: a share of the included amount, capped at full; empty with no maximum. */
const fill = computed(() => {
    if (props.included === null) {
        return 0
    }
    if (props.included <= 0) {
        return props.used > 0 ? 100 : 0
    }
    return Math.min(100, (props.used / props.included) * 100)
})

const label = computed(() =>
    props.included === null ? `${props.used} ${props.unit}` : `${props.used} of ${props.included} ${props.unit}`,
)
</script>

<template>
    <div :class="['zh-usage-meter', `zh-usage-meter--${tone}`]">
        <div
            role="meter"
            class="zh-usage-meter__track"
            :aria-valuenow="used"
            aria-valuemin="0"
            :aria-valuemax="included === null ? undefined : included"
            :aria-label="unit"
            :aria-valuetext="label"
        >
            <div class="zh-usage-meter__fill" :style="{ width: `${fill}%` }" />
        </div>
        <p class="zh-usage-meter__label">{{ label }}</p>
    </div>
</template>

<style scoped>
.zh-usage-meter {
    display: grid;
    gap: 0.375rem;
}

.zh-usage-meter__track {
    height: 0.5rem;
    background-color: var(--hair);
    overflow: hidden;
}

.zh-usage-meter__fill {
    height: 100%;
    background-color: var(--zh-usage-meter-tone);
    transition: width var(--motion-ui) ease-out;
}

.zh-usage-meter__label {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: var(--tracking-system);
    color: var(--ink-2);
}

.zh-usage-meter--under {
    --zh-usage-meter-tone: var(--zh-usage-meter-under);
}

.zh-usage-meter--at {
    --zh-usage-meter-tone: var(--zh-usage-meter-at);
}

.zh-usage-meter--over {
    --zh-usage-meter-tone: var(--zh-usage-meter-over);
}

.zh-usage-meter--over .zh-usage-meter__label {
    color: var(--zh-usage-meter-over);
}
</style>
